import {assertUnreachable} from '@wandb/weave/common/util/types';
import {toast} from 'react-toastify';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {type GenericWebhook} from '../../../Webhooks/types';
import {type JobDetails} from '../../JobDetail/utilTypes';
import {type QueueOption} from '../../SelectQueue';
import {
  getSlackNotificationConfig,
  type NotificationConfig,
} from '../notificationUtils';
import {getWebhookActionDetails} from '../TriggersTable/utilData';
import {type UserCreatableActionType} from '../TriggersTable/utilTypes';
import {type CreateTriggerDrawerInit} from './EventStore';
import {type JobOption, type TriggerFeatureFlags} from './types';

export type JobConfig = {
  targetProjectName: string | undefined;
  sourceProjectName: string | undefined;
  queuedJob: JobDetails | null;
  queuedJobAlias: string | null;
  queuedJobID: JobOption | null;
  overrides: string;
  overridesError: string;
  selectedQueue: QueueOption | null;
};

export type WebhookConfig = {
  webhook: GenericWebhook | undefined;
  webhookPayload: string;
  webhookPayloadError: string;
};

export type ARIAConfig = {
  prompt: string;
  promptError: string;
};

export const MAX_ARIA_PROMPT_LENGTH = 4000;

export const ariaPromptError = (prompt: string): string => {
  if (prompt.trim().length === 0) {
    return 'Prompt cannot be empty';
  }
  if (prompt.length > MAX_ARIA_PROMPT_LENGTH) {
    return `Prompt must be ${MAX_ARIA_PROMPT_LENGTH} characters or fewer`;
  }
  return '';
};

/*
A quick note on the zustand store here: it is not as good of an example as EventStore.tsx,
so I'd encourage you to look at that file if you just want to learn about zustand.

The big difference is that this is just managing state, with business logic in the components,
but we'd prefer to move more logic into the store's actions.
*/

export type ActionConfig = {
  doAsyncInit: (
    featureFlags: TriggerFeatureFlags,
    initProps: CreateTriggerDrawerInit,
    onClose: () => void
  ) => Promise<void>;
  actionType: UserCreatableActionType | undefined;
  // Note that the entityName is also present in the EventConfig store.
  // Why repeat? We want to allow the user to configure the entity where they
  // listen to events and the entit where they trigger actions separately.
  // this one *is* a value we expect users to change (sometimes) as
  // they configure the automation.
  entityName: string | undefined;
  setEntityName: (entityName: string | undefined) => void;

  job: JobConfig;
  updateJobConfig: (jobConfig: Partial<JobConfig>) => void;
  webhook: WebhookConfig;
  updateWebhookConfig: (webhookConfig: Partial<WebhookConfig>) => void;
  notification: NotificationConfig;
  updateNotificationConfig: (
    notificationConfig: Partial<NotificationConfig>
  ) => void;
  aria: ARIAConfig;
  setARIAPrompt: (prompt: string) => void;

  setActionType: (actionType: UserCreatableActionType) => void;
  featureFlags: TriggerFeatureFlags;
};

// make sure we return a new object each time - need new copies of all
// the child objects
const getInitialActionConfig = () => {
  return {
    entityName: undefined,
    job: {
      targetProjectName: undefined,
      sourceProjectName: undefined,
      queuedJob: null,
      queuedJobAlias: null,
      queuedJobID: null,
      overrides: '',
      overridesError: '',
      selectedQueue: null,
    },
    actionType: undefined,
    webhook: {
      webhook: undefined,
      webhookPayload: '{\n\n\n\n\n}',
      webhookPayloadError: '',
    },
    notification: {
      teamSlackIntegration: undefined,
      entityName: undefined,
    },
    aria: {
      prompt: '',
      promptError: '',
    },
    featureFlags: {},
  };
};

export const useActionConfigStore = create<ActionConfig>()(
  devtools(
    (set, get) => ({
      ...getInitialActionConfig(),
      doAsyncInit: async (
        featureFlags: TriggerFeatureFlags,
        initProps: CreateTriggerDrawerInit,
        onClose: () => void
      ) => {
        set({
          ...getInitialActionConfig(),
          featureFlags,
          entityName: initProps.actionEntityName,
          actionType: initProps.defaultAction,
        });
        if (initProps.isUpdating) {
          await ActionStoreActions.private.initForEditTrigger(
            initProps,
            onClose
          );
        }
      },
      setEntityName: (entityName: string | undefined) => {
        set({
          // reset since config will be different when changing entity
          ...getInitialActionConfig(),
          entityName,
          // these should be persisted from previous state
          featureFlags: get().featureFlags,
          actionType: get().actionType,
        });
      },
      updateJobConfig: (newJob: Partial<JobConfig>) =>
        set(state => ({job: {...state.job, ...newJob}})),
      updateWebhookConfig: (newWebhook: Partial<WebhookConfig>) =>
        set(state => ({webhook: {...state.webhook, ...newWebhook}})),
      updateNotificationConfig: (
        newNotification: Partial<NotificationConfig>
      ) =>
        set(state => ({
          notification: {...state.notification, ...newNotification},
        })),
      setARIAPrompt: (prompt: string) =>
        set(state => ({
          aria: {
            ...state.aria,
            prompt,
            promptError: ariaPromptError(prompt),
          },
        })),
      setActionType: (actionType: UserCreatableActionType) => {
        set({actionType});
        switch (actionType) {
          case 'NotificationTriggeredAction':
          case 'GenericWebhookTriggeredAction':
          case 'QueueJobTriggeredAction':
          case 'PushNotificationTriggeredAction':
          case 'ARIATriggeredAction':
            break;
          default:
            assertUnreachable(actionType);
        }
      },
    }),
    {name: 'ActionConfigStore'}
  )
);

const actionSet = useActionConfigStore.setState;

export const ActionStoreActions = {
  private: {
    initForEditTrigger: async (
      initProps: CreateTriggerDrawerInit,
      onClose: () => void
    ) => {
      if (!initProps.isUpdating) {
        throw new Error('initForEditTrigger called with isUpdating: false');
      }
      if (!initProps.trigger.action) {
        throw new Error('initForEditTrigger called with no action');
      }
      const actionType = initProps.trigger.action.actionType;
      switch (actionType) {
        case 'GenericWebhookTriggeredAction': {
          const {
            webhook,
            actionConfigString,
            entityName: webhookEntityName,
          } = getWebhookActionDetails(initProps.trigger);
          actionSet({
            actionType: 'GenericWebhookTriggeredAction',
            webhook: {
              webhook: webhook,
              webhookPayload: actionConfigString,
              webhookPayloadError: '',
            },
            entityName: webhookEntityName,
          });
          break;
        }
        case 'NotificationTriggeredAction': {
          const {teamSlackIntegration, entityName: slackEntityName} =
            getSlackNotificationConfig(initProps.trigger);

          actionSet({
            actionType: 'NotificationTriggeredAction',
            notification: {
              teamSlackIntegration,
            },
            entityName: slackEntityName,
          });
          break;
        }
        case 'QueueJobTriggeredAction': {
          const message = 'Editing Job launch automations is not supported.';
          toast.info(message);
          onClose();
          throw new Error(message);
        }
        case 'PushNotificationTriggeredAction': {
          const message =
            'Editing push notification automations is not supported.';
          toast.info(message);
          onClose();
          throw new Error(message);
        }
        case 'ARIATriggeredAction': {
          const prompt = initProps.trigger.action.spec.prompt;
          actionSet({
            actionType: 'ARIATriggeredAction',
            aria: {
              prompt,
              promptError: ariaPromptError(prompt),
            },
          });
          break;
        }
        case 'NoOpTriggeredAction': {
          const message = 'Editing no-action automations is not supported.';
          toast.info(message);
          onClose();
          throw new Error(message);
        }
        case undefined:
          toast.error(
            'There was an unexpected error loading this automation. Please contact support if you see this repeatedly.'
          );
          return;
        default:
          assertUnreachable(actionType);
      }
    },
  },
};
// we used to validate the that queue.value != '', but that is the value used
// for the starter queue option, so we don't want to reject that.
export const isValidQueue = (queue: QueueOption | null) => queue != null;

export const isValidWebhookConfig = (config: WebhookConfig) => {
  return (
    config.webhook != null &&
    config.webhook.id != null &&
    config.webhook.id !== '' &&
    config.webhookPayload != null &&
    config.webhookPayloadError === ''
  );
};

export const isValidNotificationConfig = (config: NotificationConfig) => {
  return (
    config.teamSlackIntegration != null &&
    config.teamSlackIntegration.channelName != null &&
    config.teamSlackIntegration.channelName !== '' &&
    config.teamSlackIntegration.id != null &&
    config.teamSlackIntegration.id !== ''
  );
};

export const isValidARIAConfig = (config: ARIAConfig) =>
  ariaPromptError(config.prompt) === '';

export const isValidActionConfig = (actionConfig: ActionConfig) => {
  switch (actionConfig.actionType) {
    case 'QueueJobTriggeredAction':
      return (
        actionConfig.job.queuedJob != null &&
        actionConfig.job.targetProjectName != null &&
        actionConfig.job.sourceProjectName != null &&
        isValidQueue(actionConfig.job.selectedQueue) &&
        actionConfig.job.overridesError === ''
      );
    case 'GenericWebhookTriggeredAction':
      return isValidWebhookConfig(actionConfig.webhook);
    case 'NotificationTriggeredAction':
      return isValidNotificationConfig(actionConfig.notification);
    case 'PushNotificationTriggeredAction': // push notifications cannot be created from the web UI
      return false;
    case 'ARIATriggeredAction':
      return isValidARIAConfig(actionConfig.aria);
    case undefined: // occurs when user has not selected an action type
      return false;
    default:
      assertUnreachable(actionConfig.actionType);
  }
};
